import { LegalLayout, H2, P, Ul, Li, A } from "./LegalLayout.jsx";
import { PRIVACY_EMAIL } from "../contact.js";

const privacyContact = PRIVACY_EMAIL
  ? <A href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</A>
  : <>the contact route shown in the NomLoop app (Settings)</>;

export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="22 October 2026">

      <P>NomLoop ("we", "us", "our") is a UK-based meal planning service. This
      policy explains what personal data we collect, why, and how you can control it.
      We are committed to the UK GDPR and the Data Protection Act 2018.</P>

      <H2>1. Who we are</H2>
      <P>NomLoop operates this service. For data protection queries, use{" "}
      {privacyContact}. We will respond within 30 days.</P>

      <H2>2. What data we collect</H2>
      <Ul>
        <Li><strong>Email address</strong> — when you sign in via magic link or
        OAuth (Google / Apple). Used to identify your account.</Li>
        <Li><strong>Meal preferences</strong> — diet, cuisines, allergens, dislikes,
        household size, weekly budget. Stored to personalise your plan.</Li>
        <Li><strong>Session tokens</strong> — short-lived random identifiers stored
        in memory (not on disk) to secure API calls.</Li>
        <Li><strong>Click data</strong> — when you tap a grocery link, we log the
        retailer, product SKU, and a hashed version of your IP. This enables us to
        reconcile affiliate commissions.</Li>
        <Li><strong>Analytics</strong> — only if you accept analytics cookies. We
        collect page views and feature usage. No individual behaviour is sold.</Li>
      </Ul>

      <H2>3. Why we process your data (legal basis)</H2>
      <Ul>
        <Li><strong>Contract performance</strong> — to deliver the meal planning
        and shopping list service you signed up for.</Li>
        <Li><strong>Legitimate interests</strong> — to detect abuse, reconcile
        affiliate commissions, and improve the service.</Li>
        <Li><strong>Consent</strong> — analytics cookies, where you've opted in.</Li>
      </Ul>

      <H2>4. Affiliate disclosure</H2>
      <P>NomLoop does not currently take part in an affiliate programme. If we do,
      we may earn a commission when you click a grocery link and make a purchase,
      using <A href="https://www.awin.com">Awin</A> as the affiliate network.
      Retailer recommendations are <strong>not influenced by
      commission rates</strong> — they are based solely on price and ingredient
      availability for your meal plan. If we join a programme, UK advertising law
      (CAP Code) requires us to disclose it.</P>

      <H2>5. Data sharing</H2>
      <P>We do not sell your personal data. We share data only with:</P>
      <Ul>
        <Li><strong>Render</strong> — cloud hosting for our server and database
        (EU / US regions).</Li>
        <Li><strong>Resend</strong> — transactional email (sign-in links, weekly
        plan emails). Receives only your email address.</Li>
        <Li><strong>Awin</strong> — affiliate network. Receives click identifiers
        only (no email, no name).</Li>
        <Li><strong>Analytics provider</strong> — only if you accepted analytics
        cookies. No email or identifiers shared.</Li>
      </Ul>

      <H2>6. How long we keep your data</H2>
      <Ul>
        <Li>Account data — until you delete your account.</Li>
        <Li>Click logs — 365 days, then deleted automatically.</Li>
        <Li>Magic-link tokens — 15 minutes, then deleted.</Li>
        <Li>Analytics data — per your analytics provider's retention policy.</Li>
      </Ul>

      <H2>7. Your rights</H2>
      <P>Under UK GDPR you have the right to: access your data, correct it, delete
      it, restrict its processing, and data portability. Use{" "}
      {privacyContact} to exercise any of these rights; you can also delete your
      account and data from Settings in the app. You can also complain to the{" "}
      <A href="https://ico.org.uk">ICO</A>.</P>

      <H2>8. Cookies</H2>
      <P>See our <A href="/cookies">Cookie Policy</A> for full details on what
      cookies we use and how to control them.</P>

      <H2>9. Changes to this policy</H2>
      <P>We'll notify signed-in users by email if we make material changes. The
      "Last updated" date at the top of this page reflects the most recent revision.</P>

    </LegalLayout>
  );
}
